import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { nicknameOnly } from '../../utils/clientIdentity';

const PAGE_SIZE = 25;

export default function QuotationList() {
  const navigate = useNavigate();
  const { isAgent, agentRefId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);

    // For agents, restrict to their clients' pro formas
    let agentClientIds = null;
    if (isAgent && agentRefId) {
      const { data: ac } = await supabase.from('clients').select('id').eq('care_of_agent_id', agentRefId);
      agentClientIds = (ac || []).map(c => c.id);
    }

    // If agent has no clients, nothing to show
    if (isAgent && agentClientIds !== null && agentClientIds.length === 0) {
      setRows([]); setTotal(0); setLoading(false); return;
    }

    let clientIds = [];
    if (q) {
      const { data: mc } = await supabase.from('clients').select('id').ilike('full_name', `%${q}%`).limit(50);
      clientIds = (mc || []).map(c => c.id);
    }
    let query = supabase.from('quotations')
      .select('*, clients(full_name, client_code, agents:care_of_agent_id(tls_code)), currency:currency_id(code)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (isAgent && agentClientIds) {
      query = query.in('client_id', agentClientIds);
    }

    if (q) {
      const orParts = `quotation_number.ilike.%${q}%`;
      query = clientIds.length > 0
        ? query.or(`${orParts},client_id.in.(${clientIds.join(',')})`)
        : query.or(orParts);
    }
    const { data, count } = await query;
    setRows((data || []).sort((a, b) => {
      if (a.status === 'VOID' && b.status !== 'VOID') return 1;
      if (a.status !== 'VOID' && b.status === 'VOID') return -1;
      return 0;
    }));
    setTotal(count || 0);
    setLoading(false);
  }, [q, page, isAgent, agentRefId]);

  useEffect(() => { setPage(0); }, [q]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Pro Forma Invoices</div>
          <div className="page-subtitle">PRO series — no accounting until converted to invoice</div>
        </div>
        <Link to="/quotations/new" className="btn btn-primary">+ New Pro Forma</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="form-input" placeholder="Search quotation # or client…" value={q}
            onChange={e => setQ(e.target.value)} style={{ maxWidth: 300 }} />
        </div>

        {loading ? <div className="spinner" /> : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Quotation #</th>
                    <th>Client</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Pax</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Valid Until</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} style={{ cursor: 'pointer', opacity: r.status === 'VOID' ? 0.5 : 1 }} onClick={() => navigate(`/quotations/${r.id}`)}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{r.quotation_number}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          {r.clients?.agents?.tls_code && <span style={{ color: '#f07040', fontWeight: 600 }}>{r.clients.agents.tls_code}</span>}
                          <span>{nicknameOnly(r.clients?.client_code) || r.clients?.full_name || '—'}</span>
                        </div>
                        {r.clients?.full_name && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.clients.full_name}</div>}
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{r.item_type}</td>
                      <td style={{ textAlign: 'right' }}>{r.num_pax}</td>
                      <td style={{ textAlign: 'right' }} className="amount">
                        {r.currency?.code} {parseFloat(r.total_amount || 0).toLocaleString()}
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>{r.valid_until || '—'}</td>
                      <td>
                        {r.status === 'VOID' && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginRight: 8 }}>VOID</span>}
                        {r.status === 'CONVERTED' && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginRight: 8 }}>CONVERTED</span>}
                        <Link to={`/quotations/${r.id}`} className="btn btn-secondary btn-sm"
                          onClick={e => e.stopPropagation()}>View</Link>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8}>
                      <div className="empty-state"><div className="empty-state-title">No quotations found</div></div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
                <span>{total} records · Page {page + 1} of {totalPages}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(0)} disabled={page === 0}>«</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Prev</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next ›</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
